import type { Segment } from "@pra/shared/battle-log";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { CardList, ErrorBox, PageHead, ResultBadge } from "../components/Bits";
import { Badge, Button, Card, Input, Select, Tabs } from "../design-system/components";
import { endpoints } from "../lib/api";
import { END_REASON_LABEL, formatDateTime } from "../lib/format";

function DecklistEditor({
  gameId,
  side,
  existing,
}: {
  gameId: string;
  side: "me" | "opponent";
  existing: { cardCount: number; complete: boolean; source: string } | undefined;
}) {
  const client = useQueryClient();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const save = useMutation({
    mutationFn: () => endpoints.attachDecklist(gameId, side, text),
    onSuccess: () => {
      setOpen(false);
      setText("");
      void client.invalidateQueries();
    },
  });
  return (
    <div style={{ marginTop: 12 }}>
      {existing && (
        <p className="pr-help">
          {existing.cardCount} cards · {existing.complete ? "complete" : "partial"} · from{" "}
          {existing.source}
        </p>
      )}
      {!open ? (
        <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
          {existing ? "Replace decklist" : "Add decklist"}
        </Button>
      ) : (
        <div className="pr-form">
          <Input
            as="textarea"
            rows={8}
            placeholder={"4 Ralts SVI 84\n3 Kirlia SVI 85\n…"}
            value={text}
            onChange={(e) => setText(e.target.value)}
            style={{ fontFamily: "var(--font-mono)", fontSize: 12 }}
          />
          {save.error && <ErrorBox error={save.error} />}
          <div className="pr-inline">
            <Button
              size="sm"
              onClick={() => save.mutate()}
              disabled={save.isPending || text.trim().length === 0}
            >
              Save
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function TurnLog({ segments, me }: { segments: Segment[]; me: string | undefined }) {
  return (
    <div>
      {segments.map((seg) => (
        <div
          key={`${seg.title}#${seg.entries[0]?.line ?? -1}`}
          className={[
            "pr-turn",
            seg.kind === "turn" && seg.player === me ? "pr-turn--me" : "",
            seg.kind === "checkup" ? "pr-turn--checkup" : "",
          ].join(" ")}
        >
          <h4>{seg.kind === "turn" ? `Turn ${seg.turnNumber} · ${seg.player}` : seg.title}</h4>
          <ul>
            {seg.entries.map((entry) => (
              <li key={entry.line}>
                {entry.text}
                {entry.subs.length > 0 && (
                  <ul>
                    {entry.subs.map((sub) => (
                      <li key={sub.line}>
                        {sub.text}
                        {sub.details.length > 0 && (
                          <ul>
                            {sub.details.map((d) => (
                              <li key={d}>{d}</li>
                            ))}
                          </ul>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

export function GameDetailPage() {
  const { gameId = "" } = useParams();
  const client = useQueryClient();
  const navigate = useNavigate();
  const game = useQuery({ queryKey: ["game", gameId], queryFn: () => endpoints.game(gameId) });
  const decks = useQuery({ queryKey: ["decks"], queryFn: endpoints.decks });
  const seasonsQuery = useQuery({ queryKey: ["seasons"], queryFn: endpoints.seasons });
  const setSeason = useMutation({
    mutationFn: (seasonId: string | null) => endpoints.setGameSeason(gameId, seasonId),
    onSuccess: () => void client.invalidateQueries(),
  });
  const [tab, setTab] = useState("summary");
  const [sideChoice, setSideChoice] = useState("");
  const [deckChoice, setDeckChoice] = useState("");
  const [newDeckName, setNewDeckName] = useState("");
  const setSide = useMutation({
    mutationFn: () =>
      endpoints.setSide(gameId, {
        mySide: Number.parseInt(sideChoice, 10),
        ...(deckChoice && deckChoice !== "__new" ? { deckId: deckChoice } : {}),
        ...(deckChoice === "__new" && newDeckName ? { newDeckName } : {}),
      }),
    onSuccess: () => void client.invalidateQueries(),
  });
  const remove = useMutation({
    mutationFn: () => endpoints.deleteGame(gameId),
    onSuccess: () => {
      void client.invalidateQueries();
      navigate("/games");
    },
  });
  if (game.error) return <ErrorBox error={game.error} />;
  const g = game.data;
  if (!g) return <div className="pr-empty">Loading…</div>;
  const me = g.mySide === null ? undefined : g.players[g.mySide];
  const segments = g.segments as Segment[];

  return (
    <div className="rk-stack">
      <PageHead
        title={`${me ?? g.players[0]} vs ${g.opponentName ?? g.players[1]}`}
        subtitle={
          <>
            {formatDateTime(g.playedAt)} · {END_REASON_LABEL[g.endReason]} · {g.turnCount} turns{" "}
            {g.matchId ? `· match ${g.matchId.slice(0, 8)}` : ""}
          </>
        }
        actions={
          <>
            <ResultBadge game={g} />
            <Badge variant={g.exportVariant === "debug" ? "accent" : "outline"}>
              {g.exportVariant}
            </Badge>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => remove.mutate()}
              disabled={remove.isPending}
            >
              Delete
            </Button>
          </>
        }
      />
      {g.mySide === null && (
        <Card
          title="Which side is yours?"
          description="We couldn't detect it from this log. Results and deck stats stay unknown until you pick."
        >
          <div className="pr-form" style={{ maxWidth: 420 }}>
            <Select
              value={sideChoice}
              onChange={setSideChoice}
              placeholder="Choose your player"
              options={g.players.map((p, i) => ({ value: String(i), label: p }))}
            />
            <Select
              value={deckChoice}
              onChange={setDeckChoice}
              placeholder="Link to a deck (optional)"
              options={[
                ...(decks.data?.decks ?? []).map((d) => ({ value: d.deckId, label: d.name })),
                { value: "__new", label: "+ New deck…" },
              ]}
            />
            {deckChoice === "__new" && (
              <Input
                label="New deck name"
                value={newDeckName}
                onChange={(e) => setNewDeckName(e.target.value)}
              />
            )}
            {setSide.error && <ErrorBox error={setSide.error} />}
            <Button
              onClick={() => setSide.mutate()}
              disabled={sideChoice === "" || setSide.isPending}
            >
              Save
            </Button>
          </div>
        </Card>
      )}
      <Tabs
        tabs={[
          { value: "summary", label: "Summary" },
          { value: "decks", label: "Decklists" },
          { value: "log", label: "Turn log" },
        ]}
        value={tab}
        onValueChange={setTab}
      />
      {tab === "summary" && (
        <div className="rk-split">
          <Card title="You" description={me ?? "unknown side"}>
            <dl className="pr-kv">
              <dt>Deck</dt>
              <dd>
                {g.deckId ? (
                  <Link to={`/decks/${g.deckId}`}>
                    {g.deckName ?? "Deck"}
                    {g.deckVersion ? ` · v${g.deckVersion}` : ""}
                  </Link>
                ) : (
                  "—"
                )}
              </dd>
              <dt>Turn order</dt>
              <dd>
                {g.wentFirst === undefined ? "—" : g.wentFirst ? "Went first" : "Went second"}
                {g.wonCoinToss !== undefined ? ` (${g.wonCoinToss ? "won" : "lost"} the toss)` : ""}
              </dd>
              <dt>Prizes taken</dt>
              <dd>{g.stats.me?.prizesTaken ?? "—"}</dd>
              <dt>Damage dealt</dt>
              <dd>{g.stats.me?.damageDealt ?? "—"}</dd>
              <dt>Knockouts</dt>
              <dd>{g.stats.me?.knockouts ?? "—"}</dd>
              <dt>Cards drawn</dt>
              <dd>{g.stats.me?.cardsDrawn ?? "—"}</dd>
              <dt>Energy attached</dt>
              <dd>{g.stats.me?.energyAttached ?? "—"}</dd>
              <dt>Attacks used</dt>
              <dd>{g.stats.me?.attacks.join(", ") || "—"}</dd>
              <dt>Elo</dt>
              <dd>
                {g.elo ? (
                  <>
                    {g.elo.previousElo} → <strong>{g.elo.newElo}</strong>{" "}
                    <Badge variant={g.elo.delta >= 0 ? "success" : "error"}>
                      {g.elo.delta >= 0 ? "+" : ""}
                      {g.elo.delta}
                    </Badge>
                    {g.elo.mode ? <span className="pr-muted"> · {g.elo.mode}</span> : null}
                  </>
                ) : (
                  "—"
                )}
              </dd>
              <dt>Season</dt>
              <dd>
                <div className="pr-inline">
                  <div style={{ minWidth: 200 }}>
                    <Select
                      size="sm"
                      value={g.seasonId ?? ""}
                      onChange={(v) => setSeason.mutate(v === "" ? null : v)}
                      placeholder="No season"
                      options={[
                        { value: "", label: "No season" },
                        ...(seasonsQuery.data?.seasons ?? []).map((s) => ({
                          value: s.seasonId,
                          label: `${s.name} (#${s.seasonId})`,
                        })),
                      ]}
                    />
                  </div>
                  {g.seasonSource === "user" && <Badge variant="outline">set by you</Badge>}
                </div>
              </dd>
            </dl>
            {setSeason.error && <ErrorBox error={setSeason.error} />}
            {g.inferredPrizes && (
              <>
                <h4 style={{ margin: "16px 0 8px", fontSize: 13 }}>Inferred prize cards</h4>
                <CardList cards={g.inferredPrizes} />
              </>
            )}
            {g.prizeCardsTaken && (
              <>
                <h4 style={{ margin: "16px 0 8px", fontSize: 13 }}>Prizes you took</h4>
                <CardList cards={g.prizeCardsTaken} />
              </>
            )}
          </Card>
          <Card title="Opponent" description={g.opponentName ?? "—"}>
            <dl className="pr-kv">
              <dt>Archetype</dt>
              <dd>{g.opponentArchetype ?? "—"}</dd>
              <dt>Deck name</dt>
              <dd>{g.opponentDeckMeta?.deckName ?? "—"}</dd>
              <dt>Prizes taken</dt>
              <dd>{g.stats.opponent?.prizesTaken ?? "—"}</dd>
              <dt>Damage dealt</dt>
              <dd>{g.stats.opponent?.damageDealt ?? "—"}</dd>
              <dt>Knockouts</dt>
              <dd>{g.stats.opponent?.knockouts ?? "—"}</dd>
              <dt>Attacks used</dt>
              <dd>{g.stats.opponent?.attacks.join(", ") || "—"}</dd>
            </dl>
            <h4 style={{ margin: "16px 0 8px", fontSize: 13 }}>Cards seen</h4>
            <CardList cards={g.observedCards?.opponent} emptyText="Nothing observed" />
          </Card>
        </div>
      )}
      {tab === "decks" && (
        <div className="rk-split">
          <Card
            title="Your decklist"
            description={
              g.myDecklist ? undefined : "Not in this export — paste it in PTCGL format."
            }
          >
            <CardList cards={g.myDecklist?.cards ?? g.observedCards?.me} emptyText="No cards" />
            {!g.myDecklist && g.observedCards?.me && (
              <p className="pr-help">Showing cards observed during the game.</p>
            )}
            <DecklistEditor gameId={gameId} side="me" existing={g.myDecklist} />
          </Card>
          <Card
            title="Opponent decklist"
            description={
              g.opponentDecklist ? undefined : "Not in this export — add it if you know it."
            }
          >
            <CardList
              cards={g.opponentDecklist?.cards ?? g.observedCards?.opponent}
              emptyText="No cards"
            />
            {!g.opponentDecklist && g.observedCards?.opponent && (
              <p className="pr-help">Showing cards observed during the game.</p>
            )}
            <DecklistEditor gameId={gameId} side="opponent" existing={g.opponentDecklist} />
          </Card>
        </div>
      )}
      {tab === "log" && (
        <Card
          title="Turn by turn"
          description={
            g.unparsedCount > 0
              ? `${g.unparsedCount} lines were kept but not classified.`
              : undefined
          }
        >
          <TurnLog segments={segments} me={me} />
        </Card>
      )}
    </div>
  );
}
